import CommonObj from "../CommonObj";
import { encryptTimestamp } from "../components/commoncsrf/csrf-common";
import { isTrackingBlocked } from "./auditLogGate";

/**
 * Sends user interaction data to the analytics/audit endpoint.
 * Required fields are kept as requested; additional metadata is appended.
 */
const isScanInteraction = (metadata = {}) => {
  const combined = [
    metadata?.interactionType,
    metadata?.interactionName,
    metadata?.dataTrack,
    metadata?.elementId,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return /\bscan(?:ner|ning|ned)?\b|barcode|qr|camera/.test(combined);
};

const isNavigationTrack = (metadata = {}) => {
  const trackValue = String(
    metadata?.dataTrack || metadata?.interactionName || metadata?.interactionType || ""
  ).toLowerCase();

  return trackValue.startsWith("navbar-") || trackValue.startsWith("home-tile:");
};

const getEventCategory = (metadata = {}) => {
  const interactionType = String(metadata?.interactionType || "").toLowerCase();

  if (isNavigationTrack(metadata)) return "NAVIGATION";
  if (isScanInteraction(metadata)) return "EVENT_SCAN";
  if (interactionType.includes("navigation")) return "NAVIGATION";
  if (interactionType.includes("sort") || interactionType.includes("pagination")) return "EVENT_SUBMISSION";
  if (interactionType.includes("button") || interactionType.includes("tracked")) return "EVENT_SUBMISSION";
  return "EVENT_SUBMISSION";
};

const getBrowserIdentity = () => {
  if (typeof navigator === "undefined") return "Unknown";

  const ua = navigator.userAgent || "";
  const fromBrands = navigator.userAgentData?.brands;

  if (Array.isArray(fromBrands) && fromBrands.length > 0) {
    const normalizedBrands = fromBrands
      .map((entry) => ({
        brand: String(entry?.brand || "").trim(),
        version: String(entry?.version || "").trim(),
      }))
      .filter((entry) => entry.brand.length > 0)
      .filter((entry) => !/not.*brand/i.test(entry.brand));

    const chromeBrand = normalizedBrands.find((entry) => /google chrome/i.test(entry.brand));
    if (chromeBrand) return `Google Chrome ${chromeBrand.version || ""}`.trim();

    const edgeBrand = normalizedBrands.find((entry) => /microsoft edge|edge/i.test(entry.brand));
    if (edgeBrand) return `Microsoft Edge ${edgeBrand.version || ""}`.trim();

    const chromiumBrand = normalizedBrands.find((entry) => /chromium/i.test(entry.brand));
    if (chromiumBrand) return `Google Chrome ${chromiumBrand.version || ""}`.trim();

    const firstRealBrand = normalizedBrands[0];
    if (firstRealBrand) return `${firstRealBrand.brand} ${firstRealBrand.version || ""}`.trim();
  }

  const edgeMatch = ua.match(/Edg\/(\d+)/i);
  if (edgeMatch) return `Microsoft Edge ${edgeMatch[1]}`;

  const chromeMatch = ua.match(/Chrome\/(\d+)/i);
  if (chromeMatch) return `Google Chrome ${chromeMatch[1]}`;

  const firefoxMatch = ua.match(/Firefox\/(\d+)/i);
  if (firefoxMatch) return `Mozilla Firefox ${firefoxMatch[1]}`;

  const safariMatch = ua.match(/Version\/(\d+).+Safari/i);
  if (safariMatch && !/Chrome|Chromium|Edg/i.test(ua)) return `Safari ${safariMatch[1]}`;

  return "Unknown";
};

const normalizeResourceName = (value = "") => {
  const normalized = String(value || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");

  return normalized;
};

const getPageResourceName = (pathLikeValue = "") => {
  const normalized = normalizeResourceName(pathLikeValue);
  if (!normalized) return "";

  // Keep only the first route segment so /cutting/details -> cutting
  return normalized.split("/").filter(Boolean)[0] || "";
};

export const trackEvent = async ({ accessToken, userId, actorUserRole = "", metadata = {} }) => {
  const endpoint = CommonObj?.Endpoints?.TrackUserAction || "/api/UserAudit/InsertAuditLog";

  if (!endpoint) {
    return;
  }

  if (await isTrackingBlocked()) {
    return;
  }

  const eventCategory = getEventCategory(metadata || {});
  const eventType = metadata?.interactionName || metadata?.interactionType || "";
  const pageResourceName = getPageResourceName(metadata?.destinationPath || metadata?.pagePath || "");
  const resourceName = pageResourceName || normalizeResourceName(metadata?.dataTrack || "");
  const browserIdentity = getBrowserIdentity();

  const payload = {
    UserID: userId ?? "",
    UserRole: actorUserRole || "",
    EventCategory: eventCategory || "",
    EventType: eventType || "",
    ResourceName: resourceName || "",
    IPAddress: "",
    UserAgent: browserIdentity,
  };

  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    formData.append(key, value ?? "");
  });


  try {
    await fetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken || ""}`,
        "X-CSRF-Token": encryptTimestamp(),
      },
      body: formData,
      keepalive: true,
    });
  } catch (error) {
    // Tracking should never break user interactions.
    console.warn("Tracking event failed:", error);
  }
};
